import React from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { analyzeContentBlocks, createEditorialPlan } from "../editorial-analysis";
import { EditorialContent } from "../EditorialContent";
import { EditorialImage } from "../EditorialImage";
import { PostTitle } from "../PostTitle";
import { getPostText, listIgroupIdsInBody } from "../../utils/postContent";

function formatAmount(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount ?? "");
  return Number.isInteger(n) ? String(n) : String(n);
}

function IngredientGroupList({ group }) {
  if (!group) return null;
  const name = group.name || "Ingredienser";
  return (
    <Box sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
        {name}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {(group.items || []).map((item) => {
          const note = (item.note || "").trim();
          return (
            <Typography
              component="li"
              key={item.item_uuid || `${item.ingredient_name}-${item.amount}-${item.unit}`}
              variant="body2"
              sx={{ mb: 0.4, lineHeight: 1.55 }}
            >
              <Box component="strong" sx={{ fontWeight: 700 }}>
                {item.ingredient_name}
              </Box>
              {`, ${formatAmount(item.amount)} ${item.unit}`}
              {note ? ` — ${note}` : null}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
}

export function RecipePost({ post, onEdit }) {
  const plan = createEditorialPlan(post);
  const groups = plan.ingredientGroups || [];
  const yieldAmount = post?.yield_amount || plan.yieldAmount || "";
  const cookTime = post?.cook_time || plan.cookTime || "";
  const instructions = post?.instructions || plan.instructions || "";
  const focus = plan.focus;
  const focusId = plan.focusImageId;

  const storyPlan = {
    ...plan,
    blocks: (plan.blocks || []).filter((b) => {
      if (b.type === "igroup") return false;
      if (focusId && b.type === "image" && String(b.mediaUuid).toLowerCase() === focusId) {
        return false;
      }
      return true;
    }),
  };

  const instructionBlocks = analyzeContentBlocks(
    instructions,
    plan.photoMap || {},
    plan.groupMap || {},
    { skipFocusId: focusId }
  );
  const usedInInstructions = new Set(
    instructionBlocks.filter((b) => b.type === "igroup").map((b) => b.groupUuid)
  );
  for (const id of listIgroupIdsInBody(post?.body || "")) {
    if (usedInInstructions.has(id)) continue;
    const group = plan.groupMap?.[id] || {
      group_uuid: id,
      name: "Ingredienser",
      items: [],
    };
    instructionBlocks.push({ type: "igroup", groupUuid: id, group });
    usedInInstructions.add(id);
  }

  const hasInstructionBody =
    Boolean(getPostText(instructions)) ||
    instructionBlocks.some((b) => b.type === "igroup" || b.type === "image");
  const showMeta = Boolean(yieldAmount || cookTime);
  const showIngredients = groups.length > 0;
  const showFocus = Boolean(focus);
  const showRecipeCard = showFocus || showMeta || showIngredients || hasInstructionBody;
  const instructionsPlan = { ...plan, blocks: instructionBlocks };

  return (
    <Box component="article" className="post post--recipe" sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Chip size="small" label="Opskrift" color="secondary" variant="outlined" />
        {plan.isPrivate ? <Chip size="small" label="Privat" /> : null}
        {plan.visibility === "users" ? (
          <Chip size="small" label="Brugere" color="info" variant="outlined" />
        ) : null}
        {plan.dateLabel ? (
          <Typography variant="body2" color="text.secondary">
            {plan.dateLabel}
          </Typography>
        ) : null}
      </Box>
      <PostTitle title={plan.title} onEdit={onEdit} />
      {plan.author?.name ? (
        <Typography
          component={RouterLink}
          to={`/recipes?author=${encodeURIComponent(plan.author.name)}`}
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, display: "inline-block", textDecoration: "none", "&:hover": { color: "primary.main" } }}
        >
          {plan.author.name}
        </Typography>
      ) : null}

      <Box className="recipe-article">
        <EditorialContent plan={storyPlan} />
      </Box>

      {showRecipeCard ? (
        <Box className="recipe-practical" sx={{ mt: { xs: 4, sm: 5 } }}>
          <Divider
            sx={{
              mb: { xs: 3, sm: 4 },
              borderColor: "text.primary",
              borderBottomWidth: 2,
              opacity: 0.18,
            }}
          />
          <Typography
            variant="overline"
            sx={{
              display: "block",
              color: "text.secondary",
              letterSpacing: "0.12em",
              mb: 2,
            }}
          >
            Opskriften
          </Typography>

          <Paper
            variant="outlined"
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}
            >
              {plan.title}
            </Typography>

            {showFocus ? (
              <Box
                sx={{
                  mb: 2.5,
                  display: "flex",
                  justifyContent: "center",
                  "& .editorial-image": { mx: "auto", width: "100%", maxWidth: 560 },
                  "& .editorial-image img": { display: "block", mx: "auto" },
                }}
              >
                <EditorialImage
                  src={focus.public_url || `/media/${focus.media_uuid}`}
                  description={focus.description || ""}
                  role="hero"
                />
              </Box>
            ) : null}

            {showMeta ? (
              <Box sx={{ mb: showIngredients || hasInstructionBody ? 2.5 : 0 }}>
                {yieldAmount ? (
                  <Typography variant="body2" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                    <Box component="strong" sx={{ fontWeight: 700 }}>
                      Mængde:
                    </Box>{" "}
                    {yieldAmount}
                  </Typography>
                ) : null}
                {cookTime ? (
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <Box component="strong" sx={{ fontWeight: 700 }}>
                      Tilberedningstid:
                    </Box>{" "}
                    {cookTime}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            {showIngredients ? (
              <Box
                sx={{
                  mb: hasInstructionBody ? 2.5 : 0,
                  pb: hasInstructionBody ? 2.5 : 0,
                  borderBottom: hasInstructionBody ? "1px solid" : "none",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontWeight: 700, mb: 1.5, letterSpacing: "-0.02em" }}
                >
                  Ingredienser
                </Typography>
                {groups.map((group) => (
                  <IngredientGroupList key={group.group_uuid} group={group} />
                ))}
              </Box>
            ) : null}

            {hasInstructionBody ? (
              <>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontWeight: 700, mb: 1.5, letterSpacing: "-0.02em" }}
                >
                  Instruktioner
                </Typography>
                <EditorialContent plan={instructionsPlan} igroupDefaultExpanded={false} />
              </>
            ) : null}
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
}
